"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFilterSelection = useFilterSelection;
const react_1 = require("react");
const use_filter_selection_utils_1 = require("./use-filter-selection.utils");
/**
 * Manages filter selection state with a dual-mode pattern:
 * - selectAll=true: `labels` and `values` represent EXCLUDED items
 * - selectAll=false: `labels` and `values` represent SELECTED items
 */
function useFilterSelection() {
    const [isAllSelected, setIsAllSelected] = (0, react_1.useState)(false);
    const [labels, setLabels] = (0, react_1.useState)([]);
    const [values, setValues] = (0, react_1.useState)([]);
    const [deselectedValues, setDeselectedValues] = (0, react_1.useState)([]);
    const addUniqueManyFn = use_filter_selection_utils_1.addUniqueMany;
    const removeAttributeValuesManyFn = use_filter_selection_utils_1.removeAttributeValuesMany;
    const areAllSelected = isAllSelected && labels.length === 0 && values.length === 0;
    const hasAnySelection = isAllSelected || labels.length > 0 || values.length > 0 || deselectedValues.length > 0;
    const isAttributeChecked = (0, react_1.useCallback)((label) => {
        if (isAllSelected)
            return !labels.includes(label);
        return labels.includes(label);
    }, [isAllSelected, labels]);
    const isAttributeIndeterminate = (0, react_1.useCallback)((label) => {
        const prefix = (0, use_filter_selection_utils_1.getAttributePrefix)(label);
        if (isAllSelected) {
            return !labels.includes(label) && values.some((key) => key.startsWith(prefix));
        }
        return (labels.includes(label) && deselectedValues.some((key) => key.startsWith(prefix)))
            || (!labels.includes(label) && values.some((key) => key.startsWith(prefix)));
    }, [deselectedValues, isAllSelected, labels, values]);
    const isValueChecked = (0, react_1.useCallback)((attributeLabel, value) => {
        const key = (0, use_filter_selection_utils_1.getValueKey)(attributeLabel, value);
        if (isAllSelected) {
            return !labels.includes(attributeLabel) && !values.includes(key);
        }
        if (labels.includes(attributeLabel)) {
            return !deselectedValues.includes(key);
        }
        return values.includes(key);
    }, [deselectedValues, isAllSelected, labels, values]);
    const handleToggleAttribute = (0, react_1.useCallback)((label, checked) => {
        if (isAllSelected) {
            if (checked) {
                setLabels((prev) => prev.filter((l) => l !== label));
                setValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
            }
            else {
                setLabels((prev) => (0, use_filter_selection_utils_1.addUnique)(prev, label));
                setValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
            }
        }
        else if (checked) {
            setLabels((prev) => (0, use_filter_selection_utils_1.addUnique)(prev, label));
            setValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
            setDeselectedValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
        }
        else {
            setLabels((prev) => prev.filter((l) => l !== label));
            setValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
            setDeselectedValues((prev) => (0, use_filter_selection_utils_1.removeAttributeValues)(prev, label));
        }
    }, [isAllSelected]);
    const handleToggleValue = (0, react_1.useCallback)((attributeLabel, value, checked) => {
        const key = (0, use_filter_selection_utils_1.getValueKey)(attributeLabel, value);
        if (isAllSelected) {
            if (checked) {
                setValues((prev) => prev.filter((k) => k !== key));
                setLabels((prev) => prev.filter((l) => l !== attributeLabel));
            }
            else {
                setValues((prev) => (0, use_filter_selection_utils_1.addUnique)(prev, key));
            }
        }
        else if (labels.includes(attributeLabel)) {
            if (checked) {
                setDeselectedValues((prev) => prev.filter((k) => k !== key));
            }
            else {
                setDeselectedValues((prev) => (0, use_filter_selection_utils_1.addUnique)(prev, key));
            }
        }
        else if (checked) {
            setValues((prev) => (0, use_filter_selection_utils_1.addUnique)(prev, key));
        }
        else {
            setValues((prev) => prev.filter((k) => k !== key));
        }
    }, [isAllSelected, labels]);
    const handleToggleSelectAll = (0, react_1.useCallback)((visibleLabels) => {
        const scopedLabels = Array.from(new Set((visibleLabels !== null && visibleLabels !== void 0 ? visibleLabels : []).filter(Boolean)));
        if (scopedLabels.length === 0) {
            return;
        }
        const scopedSet = new Set(scopedLabels);
        const areScopedLabelsSelected = scopedLabels.every((label) => isAttributeChecked(label));
        if (isAllSelected) {
            if (areScopedLabelsSelected) {
                // When everything is globally selected, Clear Selection should clear all
                // selections across the dataset, not only currently loaded labels.
                if (labels.length === 0 && values.length === 0) {
                    setIsAllSelected(false);
                    setLabels([]);
                    setValues([]);
                    setDeselectedValues([]);
                    return;
                }
                setLabels((prev) => addUniqueManyFn(prev, scopedLabels));
            }
            else {
                setLabels((prev) => prev.filter((label) => !scopedSet.has(label)));
            }
            setValues((prev) => removeAttributeValuesManyFn(prev, scopedLabels));
            return;
        }
        if (areScopedLabelsSelected) {
            setLabels((prev) => prev.filter((label) => !scopedSet.has(label)));
            setValues((prev) => removeAttributeValuesManyFn(prev, scopedLabels));
            setDeselectedValues((prev) => removeAttributeValuesManyFn(prev, scopedLabels));
            return;
        }
        setIsAllSelected(true);
        setLabels([]);
        setValues([]);
        setDeselectedValues([]);
    }, [addUniqueManyFn, isAllSelected, isAttributeChecked, labels.length, removeAttributeValuesManyFn, values.length]);
    const restoreFromState = (0, react_1.useCallback)((filterState) => {
        var _a, _b, _c, _d, _e;
        const saved = filterState.selectedAttributes;
        if (saved === 'ALL') {
            setIsAllSelected(true);
            setLabels((_a = filterState.deselectedAttributes) !== null && _a !== void 0 ? _a : []);
            setValues((_b = filterState.deselectedValues) !== null && _b !== void 0 ? _b : []);
            setDeselectedValues([]);
        }
        else if (Array.isArray(saved) && saved.length > 0) {
            const selectedValueAttributes = new Set(((_c = filterState.selectedValues) !== null && _c !== void 0 ? _c : [])
                .map(use_filter_selection_utils_1.getAttributeFromValueKey)
                .filter((label) => !!label));
            setIsAllSelected(false);
            setLabels(saved.filter((label) => !selectedValueAttributes.has(label)));
            setValues((_d = filterState.selectedValues) !== null && _d !== void 0 ? _d : []);
            setDeselectedValues((_e = filterState.deselectedValues) !== null && _e !== void 0 ? _e : []);
        }
        else {
            setIsAllSelected(false);
            setLabels([]);
            setValues([]);
            setDeselectedValues([]);
        }
    }, []);
    return {
        isAllSelected,
        labels,
        values,
        deselectedValues,
        areAllSelected,
        hasAnySelection,
        isAttributeChecked,
        isAttributeIndeterminate,
        isValueChecked,
        handleToggleAttribute,
        handleToggleValue,
        handleToggleSelectAll,
        restoreFromState,
    };
}
